import React, { useState, useEffect } from 'react';
import api from '../services/api';

// SMS single-segment limit; messages can exceed but each 153 chars after counts
// as a new billable segment. Show the counter so staff don't accidentally send
// a 4-segment essay.
const SMS_SEGMENT = 160;

function SendNotificationModal({ appointment, practiceId, channel, onClose, onSent, intent = null }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // GH-112: per-staff preference is to NOT show editable fields by default —
  // governance for templated comms. Editing is still possible via the
  // explicit "Edit message" toggle, but the default is locked.
  const [editing, setEditing] = useState(false);
  // GH-112: after a successful send, swap the body for an inline success
  // state ("✓ Sent to …") + a Close button. Previously the modal just
  // disappeared silently — staff had no confirmation the send actually
  // went through.
  const [sentResult, setSentResult] = useState(null);

  const isEmail = channel === 'email';
  const channelLabel = isEmail ? 'Email' : 'SMS';

  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      setLoading(true);
      setError(null);
      try {
        const res = await api.previewNotificationNow(practiceId, {
          appointment_id: appointment.id,
          channel,
          ...(intent ? { intent } : {}),
        });
        if (cancelled) return;
        const p = res.data;
        setPreview(p);
        setSubject(p.subject || '');
        setBody(p.body || '');
      } catch (err) {
        if (cancelled) return;
        setError(err.body?.error || err.message || 'Failed to load preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadPreview();
    return () => { cancelled = true; };
  }, [appointment.id, channel, practiceId, intent]);

  const handleSend = async () => {
    if (!body.trim()) {
      alert(`${channelLabel} body is empty`);
      return;
    }
    if (isEmail && !subject.trim()) {
      alert('Email subject is empty');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const payload = {
        appointment_id: appointment.id,
        channel,
        body,
      };
      if (isEmail) payload.subject = subject;
      const res = await api.sendNotificationNow(practiceId, payload);
      const result = res?.data?.result;
      if (result && result.success === false) {
        // Send Now path can return success:false at the executor level
        // (suppressed, rate-limited, etc.) even though the HTTP call succeeded.
        // Surface as an inline error so the staff sees what happened instead
        // of the modal closing silently.
        const detail = result.detail || 'Unknown reason';
        const status = result.status || 'FAILED';
        setError(`${channelLabel} not delivered: ${status} — ${detail}`);
      } else {
        if (onSent) onSent({ channel });
        // GH-112: explicit success state — modal stays open so the staff
        // sees confirmation. They close via the Close button or backdrop.
        setSentResult({
          channel,
          recipient: preview?.recipient || '',
          patientName: preview?.patientName || 'patient',
        });
      }
    } catch (err) {
      const msg = err.body?.error || err.message || 'Send failed';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const segments = !isEmail && body
    ? Math.max(1, Math.ceil(body.length / SMS_SEGMENT))
    : 0;

  const consentBlocked = preview && !preview.hasConsent;
  const missingRecipient = preview && !preview.recipient;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Send {channelLabel}</div>
            <div style={styles.subtitle}>
              to {preview?.patientName || 'patient'}{preview?.recipient ? ` · ${preview.recipient}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">×</button>
        </div>

        {loading && <div style={styles.body}><div style={styles.loading}>Loading message preview…</div></div>}

        {/* GH-112: success state — replaces the body+footer entirely so
            staff see confirmation instead of the modal vanishing silently. */}
        {sentResult && (
          <>
            <div style={styles.body}>
              <div style={styles.successBox}>
                <div style={{ fontSize: 26, marginBottom: 6 }}>✓</div>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#065f46' }}>
                  {channelLabel} sent
                </div>
                <div style={{ fontSize: 13, color: '#047857', marginTop: 4 }}>
                  Delivered to {sentResult.patientName}
                  {sentResult.recipient ? ` · ${sentResult.recipient}` : ''}
                </div>
              </div>
            </div>
            <div style={styles.footer}>
              <button onClick={onClose} style={styles.sendBtn}>Close</button>
            </div>
          </>
        )}

        {!loading && !sentResult && error && (
          <div style={styles.body}>
            <div style={styles.errorBox}>{error}</div>
          </div>
        )}

        {!loading && !sentResult && !error && preview && (
          <div style={styles.body}>
            {missingRecipient && (
              <div style={styles.warnBox}>
                Patient has no {isEmail ? 'email address' : 'phone number'} on file. Add one to the patient record before sending.
              </div>
            )}
            {consentBlocked && (
              <div style={styles.warnBox}>
                {preview.consentReason || `Patient has not consented to ${channelLabel} notifications`}. Send will be suppressed.
              </div>
            )}

            {preview.templateName && (
              <div style={styles.templateLine}>
                Pre-filled from <strong>{preview.templateName}</strong>
                {preview.isDefaultTemplate ? ' (practice default)' : ''}.
                {!editing && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      style={styles.editLink}
                      disabled={sending}
                    >
                      Edit message
                    </button>
                  </>
                )}
              </div>
            )}

            {isEmail && (
              <div style={styles.field}>
                <span style={styles.label}>Subject</span>
                {editing ? (
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    style={styles.input}
                    disabled={sending}
                  />
                ) : (
                  <div style={styles.readOnlyBox}>{subject || <em style={{ color: '#9ca3af' }}>(empty)</em>}</div>
                )}
              </div>
            )}

            <div style={styles.field}>
              <span style={styles.label}>{isEmail ? 'Message' : 'SMS body'}</span>
              {editing ? (
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={isEmail ? 12 : 5}
                  style={styles.textarea}
                  disabled={sending}
                />
              ) : (
                <div style={{ ...styles.readOnlyBox, whiteSpace: 'pre-wrap', minHeight: isEmail ? 220 : 90 }}>
                  {body || <em style={{ color: '#9ca3af' }}>(empty)</em>}
                </div>
              )}
              {!isEmail && (
                <div style={styles.charCounter}>
                  {body.length} chars · {segments} segment{segments === 1 ? '' : 's'}
                </div>
              )}
            </div>
          </div>
        )}

        {!sentResult && (
          <div style={styles.footer}>
            <button onClick={onClose} style={styles.cancelBtn} disabled={sending}>
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={loading || sending || !!error || missingRecipient}
              style={{ ...styles.sendBtn, opacity: (loading || sending || !!error || missingRecipient) ? 0.5 : 1 }}
              title={consentBlocked ? 'Patient has not consented — send may be suppressed' : ''}
            >
              {sending ? 'Sending…' : `Send ${channelLabel}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '1rem',
  },
  modal: {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560,
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column',
    maxHeight: '90vh',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '1rem 1.25rem 0.75rem', borderBottom: '1px solid #e5e7eb',
  },
  title: { fontSize: 18, fontWeight: 700, color: '#0f172a' },
  subtitle: { fontSize: 13, color: '#64748b', marginTop: 2 },
  closeBtn: {
    background: 'transparent', border: 'none', fontSize: 26, lineHeight: 1,
    cursor: 'pointer', color: '#64748b', padding: '0 0.25rem',
  },
  body: { padding: '1rem 1.25rem', overflowY: 'auto', flex: 1 },
  loading: { color: '#64748b', padding: '1.5rem 0', textAlign: 'center' },
  errorBox: {
    background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem',
    borderRadius: 8, fontSize: 13,
  },
  warnBox: {
    background: '#fef3c7', color: '#92400e', padding: '0.65rem 0.9rem',
    borderRadius: 8, fontSize: 13, marginBottom: '0.75rem', lineHeight: 1.4,
  },
  templateLine: { fontSize: 12, color: '#64748b', marginBottom: '0.75rem' },
  field: { display: 'block', marginTop: '0.75rem' },
  label: {
    display: 'block', fontSize: 12, fontWeight: 600, color: '#334155',
    marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  input: {
    width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
    boxSizing: 'border-box',
  },
  // GH-112: read-only display for subject/body when the staff hasn't
  // toggled edit mode. Styled to look like a panel of the message, not
  // a disabled input — visually distinct from the editable state so
  // it's clear what will be sent vs. what could be changed.
  readOnlyBox: {
    width: '100%', padding: '0.6rem 0.75rem',
    background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 6, fontSize: 14, color: '#0f172a',
    boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.45,
  },
  editLink: {
    background: 'transparent', border: 'none', padding: 0,
    color: '#2563eb', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', textDecoration: 'underline',
  },
  successBox: {
    background: '#ecfdf5', border: '1px solid #6ee7b7',
    borderRadius: 8, padding: '1.25rem 1rem',
    textAlign: 'center',
  },
  charCounter: { fontSize: 11, color: '#64748b', marginTop: 4, textAlign: 'right' },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
    padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb',
  },
  cancelBtn: {
    padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', color: '#334155', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
  sendBtn: {
    padding: '0.5rem 1.1rem', borderRadius: 6, border: 'none',
    background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
};

export default SendNotificationModal;
